/**
 * Format price using WooCommerce settings
 *
 * @param {number} amount Price amount
 * @returns {string} Formatted price (plain text only, no HTML)
 */

function getMetaContent(name) {
	if (typeof document === 'undefined') {
		return null;
	}
	return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null;
}

function getMetaCurrencySettings() {
	const decimalsRaw = getMetaContent('caddy-currency-decimals');
	const trimRaw = getMetaContent('caddy-price-trim-zeros');
	return {
		currencySymbol: getMetaContent('caddy-currency-symbol') || '',
		currencyDecimals: decimalsRaw !== null ? parseInt(decimalsRaw, 10) : null,
		currencyDecimalSep: getMetaContent('caddy-currency-dec-sep'),
		currencyThousandSep: getMetaContent('caddy-currency-thousand-sep'),
		currencyPosition: getMetaContent('caddy-currency-position'),
		priceTrimZeros: trimRaw === '1' ? true : (trimRaw === '0' ? false : null)
	};
}

function getStoreState() {
	try {
		const { store } = window.wp?.interactivity || {};
		if (!store) return null;
		return store('caddy/cart')?.state || null;
	} catch {
		return null;
	}
}

function shouldTrimZeros(state, forcedTrimZeros = null) {
	if (typeof forcedTrimZeros === 'boolean') {
		return forcedTrimZeros;
	}
	return state?.priceTrimZeros === true;
}

export function formatPrice(amount) {
	const state = getStoreState();
	const meta = getMetaCurrencySettings();
	const num = parseFloat(amount) || 0;
	const decimals = Number.isFinite(meta.currencyDecimals) ? meta.currencyDecimals : (state?.currencyDecimals ?? 2);
	const decSep = meta.currencyDecimalSep || state?.currencyDecimalSep || '.';
	const thousSep = meta.currencyThousandSep || state?.currencyThousandSep || ',';
	const pos = meta.currencyPosition || state?.currencyPosition || 'left';
	const sym = meta.currencySymbol || state?.currencySymbol || '';

	const parts = num.toFixed(decimals).split('.');
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousSep);
	let formatted = parts.join(decSep);
	if (shouldTrimZeros(state, meta.priceTrimZeros)) {
		formatted = formatted.replace(new RegExp('\\' + decSep + '0+$'), '');
	}

	switch (pos) {
		case 'left': return sym + formatted;
		case 'right': return formatted + sym;
		case 'left_space': return sym + ' ' + formatted;
		case 'right_space': return formatted + ' ' + sym;
		default: return sym + formatted;
	}
}

/**
 * Format a price number using WooCommerce locale settings (no currency symbol).
 * Used for display values where the template adds the symbol separately.
 *
 * @param {number} amount Price amount
 * @returns {string} Formatted number (e.g., "1,200.50" or "1.200,50")
 */
export function formatPriceSmart(amount) {
	const num = parseFloat(amount) || 0;
	const state = getStoreState();
	const meta = getMetaCurrencySettings();
	const decimals = Number.isFinite(meta.currencyDecimals) ? meta.currencyDecimals : (state?.currencyDecimals ?? 2);
	const decSep = meta.currencyDecimalSep || state?.currencyDecimalSep || '.';
	const thousSep = meta.currencyThousandSep || state?.currencyThousandSep || ',';
	const parts = num.toFixed(decimals).split('.');
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousSep);
	let result = parts.join(decSep);
	if (shouldTrimZeros(state, meta.priceTrimZeros)) {
		result = result.replace(new RegExp('\\' + decSep + '0+$'), '');
	}
	return result;
}

/**
 * Convert Store API price from minor units to major units using the currency's decimal count.
 * Store API returns: price × 10^decimals (e.g. $5.49 → 549 for 2 decimals, ₹549 → 549 for 0 decimals)
 *
 * @param {string|number} priceInMinorUnits Price in minor units from Store API
 * @returns {number} Price in major units, properly rounded
 */
export function convertStoreApiPrice(priceInMinorUnits) {
	if (!priceInMinorUnits) return 0;
	const meta = getMetaCurrencySettings();
	const state = getStoreState();
	const decimals = Number.isFinite(meta.currencyDecimals) ? meta.currencyDecimals : (state?.currencyDecimals ?? 2);
	const divisor = Math.pow(10, decimals);
	return Math.round(parseFloat(priceInMinorUnits)) / divisor;
}

/**
 * Round number to 2 decimal places for currency display
 *
 * @param {number} amount Amount to round
 * @returns {number} Amount rounded to 2 decimal places
 */
export function roundToTwo(amount) {
	return Math.round(amount * 100) / 100;
}
